import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Form, FormGroup, Label, Input, Button, Col, Alert } from 'reactstrap';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import classNames from 'classnames';
import queryString from 'query-string';
import { graphql, compose } from 'react-apollo';
import { alert } from '../../actions/alerts';
import {
  getCurrentLocale,
  getUser,
} from '../../selectors';
import { emailValidation } from '../../helpers/validators';
import styles from '../login/styles.scss';
import forgotPasswordMutation from '../../queries/forgotPassword/forgotPassword.graphql';
import answerPasswordRecoveryQuestionsMutation from '../../queries/passwordRecoveryQuestions/answerPasswordRecoveryQuestions.graphql';
import AlertBadge from '../../components/Alerts/AlertBadge';

const messages = defineMessages({
  header: {
    id: 'forgotPassword.Header',
    defaultMessage: 'Forgot Password',
    description: 'Header of forgot password page',
  },
  submitText: {
    id: 'forgotPassword.SubmitText',
    defaultMessage: 'Submit',
    description: 'Submit button text for forgot password form',
  },
  invalidEmail: {
    id: 'login.invalidEmail',
    defaultMessage: 'Please enter a valid email',
    description: 'Invalid email message on login',
  },
  nameTitle: {
    id: 'forgotPassword.nameTitle',
    defaultMessage: 'Email Address',
    description: 'Username Input Label',
  },
  instructions: {
    id: 'forgotPassword.Instructions',
    defaultMessage: `Enter the email associated with your account.`,
    description: 'Placeholder email address on login page',
  },
  successHeader: {
    id: 'forgotPassword.successHeader',
    defaultMessage: 'Check your email',
    description: 'Header when the form successfully submits',
  },
  successMessages: {
    id: 'forgotPassword.SuccessMessage',
    defaultMessage: `We have sent password reset instructions
    to your email.`,
    description: 'Instructions when the form successfully submits',
  },
  successWelcomeMessages: {
    id: 'forgotPassword.SuccessWelcomeMessages',
    defaultMessage: `Check your email for further instructions on setting up your account.`,
    description: 'Instructions when the form successfully submits and the account is unclaimed',
  },
  tokenExpired: {
    id: 'forgotPassword.TokenExpired',
    defaultMessage: `Too much time has passed since you requested to
    change your password. Please send another request to change your
    password.`,
    description: 'Instructions when the password reset token has expired ',
  },
  tokenError: {
    id: 'forgotPassword.TokenError',
    defaultMessage: `An error was encountered. Please send another request
    to change your password.`,
    description: 'Instructions when the password reset token throws an error ',
  },
  submitError: {
    id: 'forgotPassword.SubmitError',
    defaultMessage: `An error was encountered. Please send another request
    to change your password.`,
    description: 'Instructions when the form throws an error ',
  },
  securityQuestionsHeader: {
    id: 'forgotPassword.SecurityQuestionsHeader',
    defaultMessage: 'Answer Security Questions',
    description: 'Header when the form successfully submits and security questions are required',
  },
  securityQuestionsInstructions: {
    id: 'forgotPassword.SecurityQuestionsInstructions',
    defaultMessage: 'Provide the answers to your security questions in order to reset your password.',
    description: 'Instructions for answering the security questions',
  },
  answerQuestionsErrorMessage: {
    id: 'forgotPassword.AnswerQuestionsErrorMessage',
    defaultMessage: 'We were unable to validate the answers to your question. Please try again.',
    description: 'Error message when submitting the security question answers',
  },
});

class ForgotPassword extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
  };

  constructor(props, context) {
    super(props, context);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.validateEmail = this.validateEmail.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSecurityQuestionsSubmit = this.onSecurityQuestionsSubmit.bind(this);
    this.state = {
      email: '',
      emailError: null,
      success: false,
      unclaimed: false,
      submitting: false,
      tokenExpired: false,
      tokenError: false,
      submitError: false,
      facilityId: undefined,
      tenantId: undefined,
    };
  }

  componentDidMount() {
    const { token } = queryString.parse(window.location.search);
    /* eslint-disable-next-line react/no-did-mount-set-state */
    this.setState({
      tokenExpired: token === 'expired',
      tokenError: token === 'error',
    });
  }

  onSubmit(event) {
    const { email } = this.state;

    if (emailValidation(email)) {
      this.setState({
        submitting: true,
        submitError: false,
      });

      this.props.forgotPassword({
        variables: {
          email,
          locale: this.props.intl.locale,
        },
      }).then((response) => {
        this.setState({
          submitting: false,
        });
        if (response.data.forgotPassword.successful) {
          this.setState({
            success: true,
            unclaimed: response.data.forgotPassword.unclaimed,
            questions: response.data.forgotPassword.questions,
            facilityId: response.data.forgotPassword.facilityId,
            tenantId: response.data.forgotPassword.tenantId,
          });
        } else {
          this.setState({
            submitError: true,
          });
        }
      }).catch(() => {
        this.setState({
          submitting: false,
          submitError: true,
        });
      });
    } else {
      this.validateEmail();
    }

    event.preventDefault();
  }

  handleEmailChange(event) {
    this.setState({
      email: event.target.value,
    }, () => {
      this.validateEmail();
    });
  }

  validateEmail() {
    const { email } = this.state;
    this.setState({
      emailError: !emailValidation(email),
    });
  }

  onSecurityQuestionsSubmit(event) {
    event.preventDefault();

    const formData = new FormData(event.target);
    const formProps = Object.fromEntries(formData);

    const {
      facilityId,
      tenantId,
    } = this.state;

    this.setState({
      submitting: true,
      submitError: false,
    });

    this.props.answerPasswordRecoveryQuestions({
      variables: {
        facilityId,
        tenantId,
        question1Id: formProps.question0Id,
        answer1: formProps.answer0.toLowerCase(),
        question2Id: formProps.question1Id,
        answer2: formProps.answer1.toLowerCase(),
      },
    }).then((response) => {
      this.setState({
        submitting: false,
      });
      if (response.data.answerPasswordRecoveryQuestions.successful) {
        window.location.href = `/update-password?resetToken=${response.data.answerPasswordRecoveryQuestions.resetToken}&fId=${facilityId}`;
      } else {
        this.setState({
          submitError: true,
        });
      }
    }).catch(() => {
      this.setState({
        submitting: false,
        submitError: true,
      });
    });
  }

  renderCheckEmail() {
    const {
      unclaimed,
    } = this.state;

    return (
      <div className={styles.root}>
        <div className={styles.container}>
          <Col xs={12} className={`${styles.loginHeader} text-center`}>
            <i className={classNames(styles.successIcon, 'fas fa-envelope-open-text')} />
            <br />
            <FormattedMessage {...messages.successHeader} />
          </Col>
          <Col xs={12} className={styles.instructions}>
            {unclaimed ? (
              <FormattedMessage {...messages.successWelcomeMessages} />
            ) : (
              <FormattedMessage {...messages.successMessages} />
            )}
          </Col>
        </div>
      </div>
    );
  }

  renderSecurityQuestions() {
    const {
      questions,
      submitting,
      submitError,
    } = this.state;
    return (
      <div className={styles.root}>
        <div className={styles.container}>
          <Col xs={12} className={`${styles.loginHeader} text-center`}>
            <i className={classNames(styles.successIcon, 'fas fa-lock')} />
            <br />
            <FormattedMessage {...messages.securityQuestionsHeader} />
          </Col>
          <Col xs={12} className={styles.instructions}>
            <FormattedMessage {...messages.securityQuestionsInstructions} />
          </Col>
          <Col xs={12}>
            <Form onSubmit={this.onSecurityQuestionsSubmit}>
              {submitError ? (
                <AlertBadge hideClose color="danger">
                  <FormattedMessage {...messages.answerQuestionsErrorMessage} />
                </AlertBadge>
              ) : null}
              {questions.map((question, index) => (
                <FormGroup key={question.id} className={styles.formGroup}>
                  <Label for={`answer${index}`} className={`${styles.label} h5`}>
                    {question.question}
                  </Label>
                  <Input
                    className={styles.input}
                    id={`answer${index}`}
                    type="text"
                    name={`answer${index}`}
                    required
                    minLength={4}
                  />
                  <Input
                    id={`question${index}Id`}
                    type="hidden"
                    name={`question${index}Id`}
                    value={question.id}
                  />
                </FormGroup>
              ))}
              <Button type="submit" block className={styles.buttonSubmit} disabled={submitting}>
                <FormattedMessage {...messages.submitText} />
              </Button>
            </Form>
          </Col>
        </div>
      </div>
    );
  }

  render() {
    const {
      emailError,
      success,
      unclaimed,
      questions,
      tokenExpired,
      tokenError,
      submitting,
      submitError,
    } = this.state;

    return (
      success ? (
        <>
          {questions ? this.renderSecurityQuestions() : this.renderCheckEmail()}
        </>
      ) : (
        <div className={styles.root}>
          <FormGroup>
            <Form className={styles.container}>
              { tokenExpired && (
                <Alert color="danger">
                  <FormattedMessage {...messages.tokenExpired} />
                </Alert>
              )}
              { tokenError && (
                <Alert color="danger">
                  <FormattedMessage {...messages.tokenError} />
                </Alert>
              )}
              { submitError && (
                <Alert color="danger">
                  <FormattedMessage {...messages.submitError} />
                </Alert>
              )}
              <Col xs={12} className={`${styles.loginHeader} text-center`}>
                <FormattedMessage {...messages.header} />
              </Col>
              <Col xs={12} className={styles.instructions}>
                <FormattedMessage {...messages.instructions} />
              </Col>
              <Col xs={12}>
                <FormGroup className={styles.formGroup}>
                  <Label for="email" className={`${styles.label} h5`}>
                    <FormattedMessage {...messages.nameTitle} />
                  </Label>
                  <Input
                    className={`${styles.input} ${emailError && styles.input_error}`}
                    id="email"
                    type="email"
                    name="email"
                    value={this.state.email}
                    onChange={this.handleEmailChange}
                    invalid={!!this.state.emailError}
                  />
                  { emailError &&
                    <div
                      className={`${styles.emailError} mt-1`}
                      role="alert"
                    >
                      <FormattedMessage {...messages.invalidEmail} />
                    </div>
                  }
                </FormGroup>
                <Button
                  type="submit"
                  block
                  className={styles.buttonSubmit}
                  onClick={this.onSubmit}
                  disabled={submitting}
                >
                  <FormattedMessage {...messages.submitText} />
                </Button>
              </Col>
            </Form>
          </FormGroup>
        </div>
      )
    );
  }
}
const mapState = state => ({
  user: getUser(state),
  currentLocale: getCurrentLocale(state),
});

const mapDispatch = dispatch => ({
  actions: bindActionCreators({ alert }, dispatch),
});

const forgotPassword = graphql(forgotPasswordMutation, {
  name: 'forgotPassword',
});

const answerPasswordRecoveryQuestions = graphql(answerPasswordRecoveryQuestionsMutation, {
  name: 'answerPasswordRecoveryQuestions',
});

export default injectIntl(compose(
  forgotPassword,
  answerPasswordRecoveryQuestions,
  connect(mapState, mapDispatch),
)((withStyles(styles)(ForgotPassword))));
